import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../App";
import { authAxios } from "../utils/auth";
import {
  ArrowLeft, Coins, ChevronDown, ChevronUp,
  Filter, RefreshCw, X, TrendingUp, TrendingDown
} from "lucide-react";
import { formatInTimezone } from "../utils/timezone";
import AdminMenu from "../components/AdminMenu";

const PromptLogAffLedger = () => {
  const navigate = useNavigate();
  const { userId: urlUserId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [totals, setTotals] = useState({ total_credits: 0, total_debits: 0, net_change: 0, transaction_count: 0 });
  const [pagination, setPagination] = useState({ skip: 0, limit: 50, has_more: false, total_count: 0 });
  const [availableTypes, setAvailableTypes] = useState([]);
  const [showTotalsDropdown, setShowTotalsDropdown] = useState(false);

  const [selectedType, setSelectedType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [userId, setUserId] = useState(urlUserId || "");

  const [appliedFilters, setAppliedFilters] = useState({ type: "", fromDate: "", toDate: "", userId: urlUserId || "" });

  const userTimezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const fetchLedger = useCallback(async (skip = 0, filters = appliedFilters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("skip", skip);
      params.append("limit", 50);
      if (filters.type) params.append("transaction_type", filters.type);
      if (filters.fromDate) params.append("from_date", new Date(filters.fromDate).toISOString());
      if (filters.toDate) params.append("to_date", new Date(filters.toDate).toISOString());
      if (filters.userId) params.append("user_id", filters.userId);

      const response = await authAxios.get(`/credits/admin/aff-ledger?${params.toString()}`);
      setTransactions(response.data.transactions || []);
      setTotals(response.data.totals || {});
      setPagination(response.data.pagination || {});
      setAvailableTypes(response.data.available_types || []);
    } catch (error) {
      console.error("Failed to fetch affiliate ledger:", error);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    if (urlUserId) {
      const initialFilters = { ...appliedFilters, userId: urlUserId };
      setAppliedFilters(initialFilters);
      fetchLedger(0, initialFilters);
    } else {
      fetchLedger(0);
    }
  }, [urlUserId]);

  const applyFilters = () => {
    const f = { type: selectedType, fromDate, toDate, userId };
    setAppliedFilters(f);
    fetchLedger(0, f);
  };

  const clearFilters = () => {
    setSelectedType(""); setFromDate(""); setToDate(""); setUserId("");
    const empty = { type: "", fromDate: "", toDate: "", userId: "" };
    setAppliedFilters(empty);
    fetchLedger(0, empty);
  };

  const hasActiveFilters = appliedFilters.type || appliedFilters.fromDate || appliedFilters.toDate || appliedFilters.userId;
  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total_count / pagination.limit);

  const formatType = (type) => {
    const c = {
      AFF_XFER: "bg-blue-500/20 text-blue-400",
      AFF_JOIN: "bg-green-500/20 text-green-400",
      AFF_SUB: "bg-cyan-500/20 text-cyan-400",
      AFF_AUTO: "bg-purple-500/20 text-purple-400",
      TRANSFER_IN: "bg-emerald-500/20 text-emerald-400",
      TRANSFER_OUT: "bg-orange-500/20 text-orange-400",
      CODE_REDEMPTION_DEBIT: "bg-red-500/20 text-red-400"
    };
    return c[type] || "bg-gray-500/20 text-gray-400";
  };

  return (
    <div className="min-h-screen bg-divine-black text-white">
      <header className="sticky top-0 z-50 bg-divine-black/95 backdrop-blur border-b border-divine-gold/20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate("/plog")} className="flex items-center gap-2 text-white/60 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Prompt Log</span>
              </button>
              <h1 className="font-cinzel text-xl text-divine-gold">Affiliate Account Ledger</h1>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  onClick={() => setShowTotalsDropdown(!showTotalsDropdown)}
                  className="flex items-center gap-4 px-4 py-2 bg-white/5 rounded-lg border border-divine-gold/30 hover:bg-white/10 transition-colors"
                  data-testid="aff-ledger-totals-btn"
                >
                  <div className="flex items-center gap-2">
                    <Coins className="w-4 h-4 text-amber-400" />
                    <span className="text-white font-medium">{totals.net_change?.toLocaleString() || 0}</span>
                    <span className="text-white/40 text-sm">net</span>
                  </div>
                  <div className="w-px h-6 bg-white/20" />
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-400" />
                    <span className="text-green-400 font-medium">+{totals.total_credits?.toLocaleString() || 0}</span>
                  </div>
                  <div className="w-px h-6 bg-white/20" />
                  <div className="flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-red-400" />
                    <span className="text-red-400 font-medium">-{totals.total_debits?.toLocaleString() || 0}</span>
                  </div>
                  {showTotalsDropdown ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
                </button>
                {showTotalsDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-72 bg-divine-charcoal border border-divine-gold/30 rounded-lg shadow-xl z-50 p-4">
                    <div className="space-y-3">
                      <div className="flex justify-between"><span className="text-white/60">Total Transactions</span><span className="text-white font-medium">{totals.transaction_count?.toLocaleString()}</span></div>
                      <div className="border-t border-white/10 pt-3">
                        <div className="flex justify-between"><span className="text-white/60">Total Credits</span><span className="text-green-400 font-medium">+{totals.total_credits?.toLocaleString()}</span></div>
                        <div className="flex justify-between mt-2"><span className="text-white/60">Total Debits</span><span className="text-red-400 font-medium">-{totals.total_debits?.toLocaleString()}</span></div>
                        <div className="flex justify-between mt-2 pt-2 border-t border-white/10">
                          <span className="text-white/60">Net Change</span>
                          <span className={`font-medium ${totals.net_change >= 0 ? 'text-amber-400' : 'text-red-400'}`}>{totals.net_change >= 0 ? '+' : ''}{totals.net_change?.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <AdminMenu />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-divine-gold" />
            <span className="text-white/80 font-medium">Filters</span>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="ml-auto flex items-center gap-1 px-2 py-1 text-xs text-red-400 hover:text-red-300 transition-colors">
                <X className="w-3 h-3" /> Clear All
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-white/60 text-xs mb-1">Type</label>
              <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} className="w-full px-3 py-2 bg-divine-charcoal border border-white/20 rounded-lg text-white text-sm focus:border-divine-gold/50 focus:outline-none" data-testid="aff-ledger-type-filter">
                <option value="">All Types</option>
                {availableTypes.map((t) => (<option key={t} value={t}>{t}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-white/60 text-xs mb-1">User ID</label>
              <input type="text" value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="Enter user ID..." className="w-full px-3 py-2 bg-divine-charcoal border border-white/20 rounded-lg text-white text-sm focus:border-divine-gold/50 focus:outline-none placeholder:text-white/30" data-testid="aff-ledger-user-filter" />
            </div>
            <div>
              <label className="block text-white/60 text-xs mb-1">From Date</label>
              <input type="datetime-local" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-full px-3 py-2 bg-divine-charcoal border border-white/20 rounded-lg text-white text-sm focus:border-divine-gold/50 focus:outline-none" />
            </div>
            <div>
              <label className="block text-white/60 text-xs mb-1">To Date</label>
              <input type="datetime-local" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-full px-3 py-2 bg-divine-charcoal border border-white/20 rounded-lg text-white text-sm focus:border-divine-gold/50 focus:outline-none" />
            </div>
            <div className="flex items-end">
              <button onClick={applyFilters} className="w-full px-4 py-2 bg-divine-gold/20 text-divine-gold border border-divine-gold/30 rounded-lg hover:bg-divine-gold/30 transition-colors text-sm font-medium" data-testid="aff-ledger-apply-btn">Apply Filters</button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white/5 rounded-lg border border-white/10 overflow-hidden" data-testid="aff-ledger-table">
          <div className="grid grid-cols-[1fr_1.2fr_0.7fr_0.6fr_1.2fr_1fr] gap-4 px-4 py-3 bg-white/5 border-b border-white/10 text-xs text-white/60 font-medium">
            <div>Time</div>
            <div>User</div>
            <div>Type</div>
            <div className="text-right">Amount</div>
            <div>Reference</div>
            <div>Details</div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12"><RefreshCw className="w-6 h-6 text-divine-gold animate-spin" /></div>
          ) : transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-white/40">
              <Coins className="w-12 h-12 mb-4 opacity-50" /><p>No transactions found</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {transactions.map((tx) => (
                <div key={tx.tx_id} className="grid grid-cols-[1fr_1.2fr_0.7fr_0.6fr_1.2fr_1fr] gap-4 px-4 py-3 hover:bg-white/5 transition-colors items-center">
                  <div className="text-sm text-white/60">{tx.created_at ? formatInTimezone(new Date(tx.created_at), userTimezone) : "—"}</div>
                  <div className="flex flex-col">
                    <span className="text-sm text-white truncate" title={tx.user_name || tx.user_id}>{tx.user_name || tx.user_id?.slice(0, 12)}</span>
                    <span className="text-xs text-white/40 truncate" title={tx.user_id}>{tx.user_id}</span>
                  </div>
                  <div><span className={`px-2 py-1 rounded text-xs font-medium ${formatType(tx.type)}`}>{tx.type}</span></div>
                  <div className={`text-right text-sm font-medium ${tx.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>{tx.amount >= 0 ? '+' : ''}{tx.amount?.toLocaleString()}</div>
                  <div className="text-xs text-white/60 truncate" title={tx.reference || "—"}>{tx.reference || "—"}</div>
                  <div className="text-xs text-white/40 truncate" title={tx.details || ""}>{tx.details || tx.metadata?.source || "—"}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.total_count > 0 && (
          <div className="flex items-center justify-between mt-4 text-sm">
            <div className="text-white/60">Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total_count)} of {pagination.total_count}</div>
            <div className="flex gap-2">
              <button onClick={() => fetchLedger(pagination.skip - pagination.limit)} disabled={pagination.skip === 0} className="px-3 py-1 bg-white/10 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20 transition-colors">Previous</button>
              <span className="px-3 py-1 text-white/60">Page {currentPage} of {totalPages}</span>
              <button onClick={() => fetchLedger(pagination.skip + pagination.limit)} disabled={!pagination.has_more} className="px-3 py-1 bg-white/10 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20 transition-colors">Next</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PromptLogAffLedger;
